import { refreshAccessTokenFromCookie } from '@/api/authRefresh'
import { getAccessToken } from '@/utils/accessToken'
import { shouldAttemptSessionRestore } from '@/utils/userSession'

const KEY_LOGGED_IN = 'loggedIn'
const KEY_ACCESS_TOKEN_EXPIRE = 'accessTokenExpireTime'
const KEY_REFRESH_TOKEN_EXPIRE = 'refreshTokenExpireTime'

function isLoggedInStored() {
  try {
    return localStorage.getItem(KEY_LOGGED_IN) === '1'
  } catch (e) {
    return false
  }
}

function getStoredTokenExpireMeta() {
  try {
    return {
      accessTokenExpireTime: localStorage.getItem(KEY_ACCESS_TOKEN_EXPIRE) || '',
      refreshTokenExpireTime: localStorage.getItem(KEY_REFRESH_TOKEN_EXPIRE) || ''
    }
  } catch (e) {
    return { accessTokenExpireTime: '', refreshTokenExpireTime: '' }
  }
}

/** Access 过期前 3 分钟主动续期，避免使用时突然 401 */
const REFRESH_MARGIN_MS = 3 * 60 * 1000
/** 无过期时间元数据时，每 12 分钟续期一次（Access 默认 15 分钟） */
const FALLBACK_INTERVAL_MS = 12 * 60 * 1000
const MIN_DELAY_MS = 60 * 1000

const AUTH_LOGOUT_EVENT = 'auth:logout'
const AUTH_LOGIN_EVENT = 'auth:login'

let refreshTimer = null
let listenersBound = false

function parseExpireMs(value) {
  if (value == null || String(value).trim() === '') return null
  const t = Date.parse(String(value).trim())
  return Number.isFinite(t) ? t : null
}

function isRefreshSessionExpired() {
  const refreshExp = parseExpireMs(getStoredTokenExpireMeta().refreshTokenExpireTime)
  return refreshExp != null && Date.now() >= refreshExp
}

function computeNextDelayMs() {
  if (isRefreshSessionExpired()) return null

  const accessExp = parseExpireMs(getStoredTokenExpireMeta().accessTokenExpireTime)
  if (accessExp != null) {
    const delay = accessExp - Date.now() - REFRESH_MARGIN_MS
    return delay > MIN_DELAY_MS ? delay : MIN_DELAY_MS
  }
  return FALLBACK_INTERVAL_MS
}

function shouldRefreshNow() {
  if (!isLoggedInStored() || isRefreshSessionExpired()) return false
  if (!getAccessToken()) return true
  const accessExp = parseExpireMs(getStoredTokenExpireMeta().accessTokenExpireTime)
  if (accessExp == null) return false
  return Date.now() >= accessExp - REFRESH_MARGIN_MS
}

async function runKeepAliveRefresh() {
  if (!isLoggedInStored() || isRefreshSessionExpired()) {
    stopSessionKeepAlive()
    return
  }
  const ok = await refreshAccessTokenFromCookie()
  if (ok) {
    scheduleSessionKeepAlive()
  }
}

/** 登录 / refresh 成功后调用，按 Access 过期时间预约下一次静默续期 */
export function scheduleSessionKeepAlive() {
  stopSessionKeepAliveTimer()
  if (!isLoggedInStored() || isRefreshSessionExpired()) return

  const delay = computeNextDelayMs()
  if (delay == null) return

  refreshTimer = window.setTimeout(() => {
    runKeepAliveRefresh()
  }, delay)
}

function stopSessionKeepAliveTimer() {
  if (refreshTimer != null) {
    window.clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

function onAppResume() {
  if (!shouldRefreshNow()) return
  refreshAccessTokenFromCookie().then((ok) => {
    if (ok) scheduleSessionKeepAlive()
  })
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    onAppResume()
  }
}

function onAuthLogin() {
  scheduleSessionKeepAlive()
}

/** 启动长期会话保活（默认 30 天）：定时续期 + 切回标签页时检查 */
export function startSessionKeepAlive() {
  if (!listenersBound && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pageshow', onAppResume)
    window.addEventListener(AUTH_LOGOUT_EVENT, stopSessionKeepAlive)
    window.addEventListener(AUTH_LOGIN_EVENT, onAuthLogin)
    listenersBound = true
  }
  if (shouldAttemptSessionRestore() && !isRefreshSessionExpired()) {
    scheduleSessionKeepAlive()
  }
}

export function stopSessionKeepAlive() {
  stopSessionKeepAliveTimer()
  if (listenersBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pageshow', onAppResume)
    window.removeEventListener(AUTH_LOGOUT_EVENT, stopSessionKeepAlive)
    window.removeEventListener(AUTH_LOGIN_EVENT, onAuthLogin)
    listenersBound = false
  }
}
